import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  TextField,
  InputAdornment,
  Grid,
  IconButton,
  alpha,
  useTheme,
  Paper,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AudioFileIcon from "@mui/icons-material/AudioFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import SubtitlesIcon from "@mui/icons-material/Subtitles";
import ArticleIcon from "@mui/icons-material/Article";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";
import EmptyState from "../../common/EmptyState";

// ─────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────

const EXT_MAP = {
  video: ["mp4", "mkv", "avi", "mov", "webm"],
  audio: ["mp3", "flac", "wav", "aac", "ogg"],
  image: ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"],
  pdf: ["pdf"],
  text: ["txt", "srt", "vtt", "ass", "ssa", "nfo", "md", "json", "xml", "csv"],
};

const getFileType = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  for (const [type, exts] of Object.entries(EXT_MAP)) {
    if (exts.includes(ext)) return type;
  }
  return "other";
};

const FILE_STYLE = {
  video: { color: "#3B82F6", Icon: VideoFileIcon },
  audio: { color: "#10B981", Icon: AudioFileIcon },
  image: { color: "#F59E0B", Icon: ImageIcon },
  pdf: { color: "#EF4444", Icon: PictureAsPdfIcon },
  text: { color: "#8B5CF6", Icon: ArticleIcon },
  srt: { color: "#8B5CF6", Icon: SubtitlesIcon },
  other: { color: "#6B7280", Icon: InsertDriveFileIcon },
};

const getFileStyle = (name = "") => {
  const ext = name.split(".").pop()?.toLowerCase();
  if (["srt", "vtt", "ass", "ssa"].includes(ext)) return FILE_STYLE.srt;
  const type = getFileType(name);
  return FILE_STYLE[type] || FILE_STYLE.other;
};

const formatSize = (bytes) => {
  if (!bytes) return "—";
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(2)} GB`;
  const mb = bytes / 1024 ** 2;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
};

// ─────────────────────────────────────────────
// Stream URL hook — gets a signed token, builds
// a direct streamable URL for video/audio
// ─────────────────────────────────────────────
const useStreamUrl = (file) => {
  const [streamUrl, setStreamUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!file) {
      setStreamUrl(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setStreamUrl(null);

    const TOKEN_PREFIX = process.env.REACT_APP_TOKEN;
    const token = localStorage.getItem(`${TOKEN_PREFIX}_user`);

    fetch(`${process.env.REACT_APP_BASE_URL}/api/torrent/stream-token`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ uid: file.download_uid, path: file.path }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        if (data.success) {
          setStreamUrl(
            `${process.env.REACT_APP_BASE_URL}/api/torrent/stream?token=${data.token}`,
          );
        }
      })
      .catch((err) => console.error("[useStreamUrl]", err.message))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [file?.download_uid, file?.path]);

  return { streamUrl, loading };
};

const canPreview = (name) =>
  ["video", "audio", "image", "text", "pdf"].includes(getFileType(name));

// ─────────────────────────────────────────────
// Auth fetch helper — attaches Bearer token
// ─────────────────────────────────────────────

const authFetch = (filePath, downloadUid, extra = "") => {
  const TOKEN_PREFIX = process.env.REACT_APP_TOKEN;
  const token = localStorage.getItem(`${TOKEN_PREFIX}_user`);
  const url = `${process.env.REACT_APP_BASE_URL}/api/torrent/file?uid=${encodeURIComponent(downloadUid)}&path=${encodeURIComponent(filePath)}${extra}`;
  return fetch(url, { headers: { Authorization: `Bearer ${token}` } });
};

// ─────────────────────────────────────────────
// Blob URL hook — fetches file with auth token
// ─────────────────────────────────────────────

const useAuthBlobUrl = (file) => {
  const [blobUrl, setBlobUrl] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    if (!file) {
      setBlobUrl(null);
      return;
    }

    let objectUrl = null;
    let cancelled = false;

    setLoading(true);
    setBlobUrl(null);

    authFetch(file.path, file.download_uid)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
      })
      .catch((err) => console.error("[useAuthBlobUrl]", err.message))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file?.download_uid, file?.path]);

  return { blobUrl, loading };
};

// ─────────────────────────────────────────────
// Auth-aware download trigger
// ─────────────────────────────────────────────

// ── NEW — get a signed token, let browser download natively ──
const triggerAuthDownload = async (filePath, downloadUid, fileName) => {
  try {
    const TOKEN_PREFIX = process.env.REACT_APP_TOKEN;
    const token = localStorage.getItem(`${TOKEN_PREFIX}_user`);

    // Step 1 — get a short-lived stream token
    const res = await fetch(
      `${process.env.REACT_APP_BASE_URL}/api/torrent/stream-token`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ uid: downloadUid, path: filePath }),
      },
    );

    const data = await res.json();
    if (!data.success) {
      console.error("[triggerAuthDownload] Failed to get token:", data.msg);
      return;
    }

    // Step 2 — open the stream URL directly in browser with download=true
    // Browser handles it natively — no RAM bloat, works for any file size
    const downloadUrl = `${process.env.REACT_APP_BASE_URL}/api/torrent/stream?token=${data.token}&download=true`;
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } catch (err) {
    console.error("[triggerAuthDownload]", err.message);
  }
};

// ─────────────────────────────────────────────
// Auth Image — loads poster with token
// ─────────────────────────────────────────────

const AuthImage = ({ file, sx, onClick }) => {
  const { blobUrl, loading } = useAuthBlobUrl(file);
  const theme = useTheme();

  if (loading) {
    return (
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          bgcolor: alpha(theme.palette.primary.main, 0.08),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          ...sx,
        }}
      >
        <CircularProgress size={16} />
      </Box>
    );
  }

  if (!blobUrl) return null;

  return (
    <Box
      component="img"
      src={blobUrl}
      alt="poster"
      onClick={onClick}
      sx={{
        width: 48,
        height: 48,
        borderRadius: 1.5,
        objectFit: "cover",
        flexShrink: 0,
        border: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
        cursor: onClick ? "pointer" : "default",
        ...sx,
      }}
    />
  );
};

// ─────────────────────────────────────────────
// File Viewer Modal
// ─────────────────────────────────────────────

const FileViewerModal = ({ file, onClose }) => {
  const theme = useTheme();
  const [textContent, setTextContent] = React.useState(null);
  const [textLoading, setTextLoading] = React.useState(false);
  const fileType = getFileType(file?.name ?? "");

  // Stream URL for video/audio — direct pipe, no memory bloat
  const { streamUrl, loading: streamLoading } = useStreamUrl(
    file && (fileType === "video" || fileType === "audio") ? file : null,
  );

  // Blob URL for image/pdf — small files, fine to buffer
  const { blobUrl, loading: blobLoading } = useAuthBlobUrl(
    file && (fileType === "image" || fileType === "pdf") ? file : null,
  );

  const loading = streamLoading || blobLoading;

  // Text files
  React.useEffect(() => {
    if (!file || fileType !== "text") return;
    setTextLoading(true);
    setTextContent(null);

    authFetch(file.path, file.download_uid)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.text();
      })
      .then((t) => setTextContent(t))
      .catch(() => setTextContent("⚠️ Could not load file content."))
      .finally(() => setTextLoading(false));
  }, [file?.download_uid, file?.path, fileType]);

  if (!file) return null;

  const { color } = getFileStyle(file.name);

  return (
    <Dialog
      open={Boolean(file)}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
          border: `1px solid ${alpha(color, 0.25)}`,
        },
      }}
    >
      {/* Header — same as before */}
      <DialogTitle
        sx={{
          p: 0,
          background: `linear-gradient(135deg, ${alpha(color, 0.12)}, ${alpha(color, 0.04)})`,
          borderBottom: `1px solid ${alpha(color, 0.15)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 2.5, py: 1.5 }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Box
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1.5,
                bgcolor: alpha(color, 0.15),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color,
              }}
            >
              {React.createElement(getFileStyle(file.name).Icon, {
                sx: { fontSize: 20 },
              })}
            </Box>
            <Box>
              <Typography
                variant="body2"
                fontWeight={700}
                sx={{ color: "text.primary", maxWidth: 420 }}
                noWrap
              >
                {file.name}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {formatSize(file.length)} · {file.torrent_name}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      {/* Content */}
      <DialogContent
        sx={{
          p: 0,
          bgcolor:
            fileType === "video" || fileType === "image"
              ? "#000"
              : "background.paper",
          minHeight: 320,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {loading && (
          <Stack alignItems="center" py={6}>
            <CircularProgress size={32} sx={{ color }} />
            <Typography variant="caption" color="text.disabled" mt={1.5}>
              {fileType === "video" || fileType === "audio"
                ? "Preparing stream…"
                : "Loading file…"}
            </Typography>
          </Stack>
        )}

        {/* VIDEO — streams directly, supports seeking */}
        {!loading && fileType === "video" && streamUrl && (
          <Box sx={{ width: "100%", lineHeight: 0 }}>
            <video
              controls
              autoPlay
              style={{ width: "100%", maxHeight: "70vh", display: "block" }}
              src={streamUrl}
            />
          </Box>
        )}

        {/* AUDIO — streams directly */}
        {!loading && fileType === "audio" && streamUrl && (
          <Box sx={{ p: 4, width: "100%", textAlign: "center" }}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: alpha(color, 0.12),
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color,
                mx: "auto",
                mb: 3,
              }}
            >
              <AudioFileIcon sx={{ fontSize: 40 }} />
            </Box>
            <audio
              controls
              style={{ width: "100%", maxWidth: 480 }}
              src={streamUrl}
            />
          </Box>
        )}

        {/* IMAGE — blob is fine, images are small */}
        {!loading && fileType === "image" && blobUrl && (
          <Box
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              p: 2,
            }}
          >
            <img
              src={blobUrl}
              alt={file.name}
              style={{
                maxWidth: "100%",
                maxHeight: "70vh",
                objectFit: "contain",
                borderRadius: 8,
              }}
            />
          </Box>
        )}

        {/* PDF — blob is fine */}
        {!loading && fileType === "pdf" && blobUrl && (
          <Box sx={{ width: "100%", height: "70vh" }}>
            <iframe
              src={blobUrl}
              title={file.name}
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </Box>
        )}

        {/* TEXT */}
        {fileType === "text" && (
          <Box sx={{ width: "100%", p: 2.5 }}>
            {textLoading ? (
              <Stack alignItems="center" py={6}>
                <CircularProgress size={32} sx={{ color }} />
                <Typography variant="caption" color="text.disabled" mt={1.5}>
                  Loading file…
                </Typography>
              </Stack>
            ) : (
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.default, 0.6),
                  maxHeight: "60vh",
                  overflow: "auto",
                  fontFamily: "monospace",
                  fontSize: "0.78rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  color: "text.primary",
                  borderColor: alpha(color, 0.2),
                }}
              >
                {textContent}
              </Paper>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Footer */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
        }}
      >
        <Button
          variant="contained"
          size="small"
          startIcon={<DownloadIcon />}
          onClick={() =>
            triggerAuthDownload(file.path, file.download_uid, file.name)
          }
          sx={{
            borderRadius: 2,
            bgcolor: color,
            "&:hover": { bgcolor: alpha(color, 0.85) },
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Download
        </Button>
        <Button
          size="small"
          onClick={onClose}
          sx={{ borderRadius: 2, textTransform: "none" }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─────────────────────────────────────────────
// File Card
// ─────────────────────────────────────────────

const FileCard = ({ file, onPreview }) => {
  const theme = useTheme();
  const { color, Icon } = getFileStyle(file.name);
  const previewable = canPreview(file.name);

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        borderRadius: 2.5,
        borderColor: alpha(color, 0.18),
        transition: "all 0.22s ease",
        overflow: "hidden",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: `0 8px 24px ${alpha(color, 0.16)}`,
          borderColor: alpha(color, 0.4),
        },
      }}
    >
      {/* Top accent bar */}
      <Box
        sx={{
          height: 3,
          background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.35)})`,
        }}
      />

      <Box sx={{ p: 1.75, flex: 1, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {/* Icon */}
          <Box
            sx={{
              width: 42,
              height: 42,
              borderRadius: 2,
              flexShrink: 0,
              background: `linear-gradient(135deg, ${alpha(color, 0.15)}, ${alpha(color, 0.05)})`,
              border: `1px solid ${alpha(color, 0.2)}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color,
            }}
          >
            <Icon sx={{ fontSize: 21 }} />
          </Box>

          {/* Info */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Tooltip title={file.name} placement="top">
              <Typography
                variant="body2"
                fontWeight={700}
                noWrap
                sx={{
                  color: "text.primary",
                  lineHeight: 1.3,
                  fontSize: "0.8rem",
                }}
              >
                {file.name}
              </Typography>
            </Tooltip>
            <Stack
              direction="row"
              spacing={0.75}
              mt={0.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Chip
                label={formatSize(file.length)}
                size="small"
                sx={{
                  height: 17,
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  bgcolor: alpha(color, 0.1),
                  color,
                  border: "none",
                }}
              />
              <Chip
                label={file.name.split(".").pop().toUpperCase()}
                size="small"
                sx={{
                  height: 17,
                  fontSize: "0.62rem",
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.text.secondary, 0.08),
                  color: "text.secondary",
                  border: "none",
                }}
              />
            </Stack>
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {previewable && (
              <Tooltip title="Preview">
                <IconButton
                  size="small"
                  onClick={() => onPreview(file)}
                  sx={{
                    width: 26,
                    height: 26,
                    border: "1px solid",
                    borderColor: alpha(color, 0.3),
                    borderRadius: 1.5,
                    color: alpha(color, 0.7),
                    "&:hover": {
                      bgcolor: alpha(color, 0.1),
                      borderColor: color,
                      color,
                    },
                  }}
                >
                  <VisibilityIcon sx={{ fontSize: 13 }} />
                </IconButton>
              </Tooltip>
            )}
            {/* ── Auth-aware download — no bare href ── */}
            <Tooltip title="Download">
              <IconButton
                size="small"
                onClick={() =>
                  triggerAuthDownload(file.path, file.download_uid, file.name)
                }
                sx={{
                  width: 26,
                  height: 26,
                  border: "1px solid",
                  borderColor: alpha(theme.palette.success.main, 0.3),
                  borderRadius: 1.5,
                  color: alpha(theme.palette.success.main, 0.7),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.success.main, 0.1),
                    borderColor: theme.palette.success.main,
                    color: "success.main",
                  },
                }}
              >
                <DownloadIcon sx={{ fontSize: 13 }} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </Box>
    </Paper>
  );
};

// ─────────────────────────────────────────────
// Torrent Group Card
// ─────────────────────────────────────────────

const TorrentGroup = ({ download, onPreview, onDelete }) => {
  const theme = useTheme();
  const files =
    typeof download.files === "string"
      ? JSON.parse(download.files)
      : (download.files ?? []);

  const posterFile = files.find((f) => getFileType(f.name) === "image");
  const totalFiles = files.length;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        borderColor: alpha(theme.palette.divider, 0.5),
        overflow: "hidden",
        mb: 2,
      }}
    >
      {/* ── Torrent Header ── */}
      <Box
        sx={{
          px: 2.5,
          py: 1.75,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.06)}, transparent)`,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        {/* Poster thumbnail — uses AuthImage to attach token */}
        {posterFile ? (
          <AuthImage
            file={{ ...posterFile, download_uid: download.uid }}
            onClick={() =>
              onPreview({
                ...posterFile,
                torrent_name: download.torrent_name,
                download_uid: download.uid,
              })
            }
          />
        ) : (
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 1.5,
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <FolderOpenIcon
              sx={{ fontSize: 24, color: theme.palette.primary.main }}
            />
          </Box>
        )}

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body1" fontWeight={700} noWrap>
            {download.torrent_name}
          </Typography>
          <Stack direction="row" spacing={1} mt={0.3} alignItems="center">
            <Chip
              label={`${totalFiles} file${totalFiles !== 1 ? "s" : ""}`}
              size="small"
              sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600 }}
            />
            <Chip
              label={formatSize(download.total_size)}
              size="small"
              color="primary"
              variant="outlined"
              sx={{ height: 18, fontSize: "0.62rem", fontWeight: 600 }}
            />
          </Stack>
        </Box>

        <Tooltip title="Delete torrent & files">
          <IconButton
            size="small"
            onClick={() => onDelete(download.uid)}
            sx={{
              width: 30,
              height: 30,
              border: "1px solid",
              borderColor: alpha(theme.palette.error.main, 0.25),
              borderRadius: 1.5,
              color: alpha(theme.palette.error.main, 0.5),
              "&:hover": {
                bgcolor: alpha(theme.palette.error.main, 0.1),
                borderColor: theme.palette.error.main,
                color: "error.main",
              },
            }}
          >
            <DeleteIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Files Grid ── */}
      <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
        <Grid container spacing={1.5}>
          {files.map((file, i) => (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              key={i}
              sx={{ display: "flex" }}
            >
              <FileCard
                file={{
                  ...file,
                  torrent_name: download.torrent_name,
                  download_uid: download.uid,
                }}
                onPreview={onPreview}
              />
            </Grid>
          ))}
        </Grid>
      </CardContent>
    </Card>
  );
};

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────

const MyFiles = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();

  const [downloads, setDownloads] = useState([]);
  const [search, setSearch] = useState("");
  const [previewFile, setPreviewFile] = useState(null);

  useEffect(() => {
    loadFiles();
  }, []);

  const loadFiles = async () => {
    const res = await hitAxios({
      path: "/api/torrent/my-downloads?limit=100",
      showSnackbar: false,
    });
    if (res?.data?.success)
      setDownloads(res.data.data.filter((d) => d.status === "completed"));
  };

  const deleteDownload = async (uid) => {
    await hitAxios({
      path: "/api/torrent/delete",
      post: true,
      obj: { uid },
    });
    loadFiles();
  };

  const filteredDownloads = downloads
    .map((dl) => {
      if (!search) return dl;
      const files =
        typeof dl.files === "string" ? JSON.parse(dl.files) : (dl.files ?? []);
      const matchedFiles = files.filter((f) =>
        f.name?.toLowerCase().includes(search.toLowerCase()),
      );
      const nameMatch = dl.torrent_name
        ?.toLowerCase()
        .includes(search.toLowerCase());
      if (!nameMatch && matchedFiles.length === 0) return null;
      return {
        ...dl,
        files: nameMatch ? dl.files : JSON.stringify(matchedFiles),
      };
    })
    .filter(Boolean);

  const totalFiles = downloads.reduce((acc, dl) => {
    const files =
      typeof dl.files === "string" ? JSON.parse(dl.files) : (dl.files ?? []);
    return acc + files.length;
  }, 0);

  return (
    <Box>
      <PageHeader
        title={lang.myFiles || "My Files"}
        subtitle={`${downloads.length} torrents · ${totalFiles} files`}
        icon={FolderIcon}
        primaryAction={
          <TextField
            size="small"
            placeholder={lang.searchFiles || "Search files or torrents..."}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: "100%", sm: 280 },
              "& .MuiOutlinedInput-root": { borderRadius: 2 },
            }}
          />
        }
      />

      {filteredDownloads.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            borderRadius: 3,
            borderColor: alpha(theme.palette.divider, 0.6),
          }}
        >
          <CardContent>
            <EmptyState
              icon={FolderIcon}
              title={lang.noCompletedDownloads || "No files yet"}
              description="Completed downloads will appear here. Start downloading a torrent to see your files."
            />
          </CardContent>
        </Card>
      ) : (
        <Box>
          {filteredDownloads.map((dl) => (
            <TorrentGroup
              key={dl.uid}
              download={dl}
              onPreview={setPreviewFile}
              onDelete={deleteDownload}
            />
          ))}
        </Box>
      )}

      {/* Viewer Modal */}
      <FileViewerModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </Box>
  );
};

export default MyFiles;
